import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import api from '../api/axios';
import AuthContext from '../context/AuthContext';
import SiteFooter from '../components/SiteFooter';
import TermsAgreement from '../components/TermsAgreement';

// 네이버 OAuth 가입 완료 페이지 (2026-04-28 변경):
// - 매장명, 대표자명, 휴대폰 번호, 사업자번호, 베이 수를 사용자가 직접 입력
// - 이전엔 Waitlist에서 매장명/대표자명 자동 prefill — 이제는 hint(placeholder)로만 노출
// - Cloudflare Turnstile 봇 검증

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const OAuthSetupPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { loginWithToken } = useContext(AuthContext);

    const pendingToken = location.state?.pendingToken;
    const hints = location.state?.hints || {};   // { shopNameHint, ownerNameHint, naverName, naverEmail }

    const [shopName, setShopName] = useState(hints.shopNameHint || '');
    const [ownerName, setOwnerName] = useState(hints.ownerNameHint || hints.naverName || '');
    const [businessNumber, setBusinessNumber] = useState('');
    const [phone, setPhone] = useState('');
    const [bayCount, setBayCount] = useState(1);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // 약관 동의 (둘 다 필수) — 미체크 시 가입 완료 버튼 비활성
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [agreedPrivacy, setAgreedPrivacy] = useState(false);
    const agreedAll = agreedTerms && agreedPrivacy;

    // Turnstile
    const turnstileRef = useRef(null);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
    const requireTurnstile = !!TURNSTILE_SITE_KEY;

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY) return;
        const SCRIPT_ID = 'cf-turnstile-script';
        const renderWidget = () => {
            if (!window.turnstile || !turnstileRef.current) return;
            const id = window.turnstile.render(turnstileRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                callback: (token) => setTurnstileToken(token),
                'expired-callback': () => setTurnstileToken(''),
                'error-callback': () => setTurnstileToken(''),
            });
            setTurnstileWidgetId(id);
        };
        if (window.turnstile) {
            renderWidget();
            return;
        }
        if (document.getElementById(SCRIPT_ID)) return;
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = renderWidget;
        document.body.appendChild(script);
        return () => {
            if (turnstileWidgetId !== null && window.turnstile) {
                try { window.turnstile.remove(turnstileWidgetId); } catch {}
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!pendingToken) {
        return (
            <Container className="py-5" style={{ maxWidth: 500 }}>
                <Alert variant="danger">
                    잘못된 접근입니다. 네이버 가입을 다시 시도해주세요.
                </Alert>
                <Button variant="primary" onClick={() => navigate('/signup')}>회원가입 페이지로</Button>
            </Container>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!shopName.trim() || !ownerName.trim() || !businessNumber.trim() || !phone.trim()) {
            setError('매장명, 대표자명, 휴대폰 번호, 사업자번호는 필수입니다.');
            return;
        }
        if (!/^01[016789]-?\d{3,4}-?\d{4}$/.test(phone.trim())) {
            setError('올바른 휴대폰 번호 형식이 아닙니다. (예: 010-1234-5678)');
            return;
        }
        if (requireTurnstile && !turnstileToken) {
            setError('봇 검증을 완료해주세요.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await api.post('/signup/oauth-complete', {
                pending_token: pendingToken,
                shopName,
                owner_name: ownerName,
                phone,
                business_number: businessNumber,
                bay_count: bayCount,
                turnstileToken,
            });
            const { accessToken } = res.data.data;
            loginWithToken(accessToken);
            navigate('/', { replace: true });
        } catch (err) {
            const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || '가입 처리에 실패했습니다.';
            setError(msg);
            if (window.turnstile && turnstileWidgetId !== null) {
                try { window.turnstile.reset(turnstileWidgetId); } catch {}
            }
            setTurnstileToken('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-form-panel" style={{ width: '100%' }}>
                <div className="login-form-wrapper" style={{ maxWidth: 480 }}>
                    <h3 className="mb-1">매장 정보 입력</h3>
                    <p className="text-muted mb-4">네이버 인증이 완료되었습니다. 매장 정보를 입력해주세요.</p>

                    {error && <Alert variant="danger">{error}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>매장명 <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={shopName}
                                onChange={(e) => setShopName(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>대표자(담당자)명 <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={ownerName}
                                onChange={(e) => setOwnerName(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>휴대폰 번호 <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="010-1234-5678"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                required
                            />
                            <Form.Text className="text-muted">
                                결제 영수증·결제 실패 안내를 받는 번호입니다.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>사업자등록번호 <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="000-00-00000"
                                value={businessNumber}
                                onChange={(e) => setBusinessNumber(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>세차 베이 수</Form.Label>
                            <Form.Select value={bayCount} onChange={(e) => setBayCount(Number(e.target.value))}>
                                {[1, 2, 3, 4].map((n) => (
                                    <option key={n} value={n}>{n}개</option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">나중에 설정에서 변경할 수 있습니다.</Form.Text>
                        </Form.Group>

                        {requireTurnstile && (
                            <div ref={turnstileRef} style={{ marginBottom: 16 }} />
                        )}

                        <TermsAgreement
                            agreedTerms={agreedTerms}
                            agreedPrivacy={agreedPrivacy}
                            onChangeTerms={setAgreedTerms}
                            onChangePrivacy={setAgreedPrivacy}
                            disabled={submitting}
                        />

                        <Button
                            type="submit"
                            variant="primary"
                            className="w-100"
                            disabled={submitting || !agreedAll || (requireTurnstile && !turnstileToken)}
                        >
                            {submitting ? <><Spinner size="sm" className="me-2" />처리 중...</> : '가입 완료'}
                        </Button>
                    </Form>
                    <SiteFooter />
                </div>
            </div>
        </div>
    );
};

export default OAuthSetupPage;
